import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Camera, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ProfileImageUploadProps {
  currentImage?: string | null;
  employeeName: string;
  employeeId: string;
  onImageUpdate: (imageUrl: string | null) => void;
}

export const ProfileImageUpload = ({ currentImage, employeeName, employeeId, onImageUpdate }: ProfileImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(currentImage || null);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "ملف كبير جداً",
        description: "يرجى اختيار صورة أصغر من 5 ميجابايت",
        variant: "destructive"
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "نوع ملف غير صحيح",
        description: "يرجى اختيار ملف صورة صالح",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${employeeId}-${Date.now()}.${fileExt}`;
      const filePath = `profiles/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('employee-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('employee-files')
        .getPublicUrl(filePath);

      // Update employee record in database
      const { error: updateError } = await supabase
        .from('employees')
        .update({ profile_image: publicUrl })
        .eq('id', employeeId);

      if (updateError) {
        throw updateError;
      }

      // Call onImageUpdate with the public URL to trigger refetch
      await onImageUpdate(publicUrl);
      setPreviewImage(publicUrl);
      setIsUploading(false);
      
      toast({
        title: "تم تحديث الصورة",
        description: "تم رفع صورتك الشخصية بنجاح"
      });

      // Clean up preview URL
      URL.revokeObjectURL(previewUrl);

    } catch (error) {
      console.error('Error uploading image:', error);
      setIsUploading(false);
      toast({
        title: "خطأ في رفع الصورة",
        description: "حدث خطأ أثناء رفع الصورة، يرجى المحاولة مرة أخرى",
        variant: "destructive"
      });
    }
  };

  const handleRemoveImage = async () => {
    try {
      setIsUploading(true);
      
      // Delete the image file from storage if it exists
      if (previewImage && previewImage.includes('employee-files')) {
        const filePath = previewImage.split('/employee-files/')[1];
        if (filePath) {
          await supabase.storage
            .from('employee-files')
            .remove([filePath]);
        }
      }

      // Update employee record in database using user_id for authentication
      const { error } = await supabase
        .from('employees')
        .update({ profile_image: null })
        .eq('id', employeeId)
        .eq('user_id', user?.id); // Add user_id check for RLS

      if (error) {
        throw error;
      }

      setPreviewImage(null);
      await onImageUpdate(null);
      
      toast({
        title: "تم حذف الصورة",
        description: "تم حذف صورتك الشخصية بنجاح"
      });
    } catch (error) {
      console.error('Error removing image:', error);
      toast({
        title: "خطأ في حذف الصورة",
        description: error instanceof Error ? error.message : "حدث خطأ أثناء حذف الصورة، يرجى المحاولة مرة أخرى",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="w-5 h-5" />
          الصورة الشخصية
        </CardTitle>
        <CardDescription>
          قم بتحديث صورتك الشخصية التي ستظهر في النظام
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-6">
          <Avatar className="w-24 h-24 border-4 border-background shadow-lg">
            {previewImage && <AvatarImage src={previewImage} alt={employeeName} />}
            <AvatarFallback className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold text-xl">
              {getInitials(employeeName)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 space-y-4">
            <div>
              <Label htmlFor="profile-image" className="block text-sm font-medium mb-2">
                اختر صورة جديدة
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="profile-image"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  className="cursor-pointer"
                />
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  disabled={isUploading}
                  className="whitespace-nowrap"
                >
                  <label htmlFor="profile-image" className="cursor-pointer flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {isUploading ? 'جاري الرفع...' : 'اختر ملف'}
                  </label>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                الحد الأقصى: 5 ميجابايت، أنواع مدعومة: JPG, PNG, GIF
              </p>
            </div>
            
            {previewImage && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRemoveImage}
                disabled={isUploading}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                حذف الصورة
              </Button>
            )}
          </div>
        </div>

        {isUploading && (
          <div className="flex items-center justify-center p-4 bg-muted rounded-lg">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-2"></div>
            <span className="text-sm text-muted-foreground">جاري رفع الصورة...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};